import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { css } from "@/styled-system/css";
import { forwardRef, useCallback } from "react";
import Source from "../Source/Source.esm.js";
import Video from "../Video/Video.esm.js";
import { v8ResponsiveToPanda } from "../../utils/token-migration/migrate-responsive.esm.js";
const baseBackgroundVideoClass = css({
  pointerEvents: "none",
  position: "absolute",
  top: "0",
  right: "0",
  bottom: "0",
  left: "0",
  width: "100%",
  height: "100%",
  // The constellation `.c11n` base reset applies `all: unset`, which wipes the
  // <video>'s user-agent `overflow: clip` — the default that crops object-fit
  // (cover/contain) overflow to the element box. Without it the scaled video
  // paints OUTSIDE its box and visually bleeds into adjacent groups even though
  // the box itself is correctly sized. Re-clip explicitly (this class is in
  // @layer utilities, so it beats the @layer base reset). Mirrors
  // GroupBackgroundImage.
  overflow: "hidden"
});
const fitPositionClass = css({
  objectFit: "[var(--gbv-fit-base)]",
  objectPosition: "[var(--gbv-pos-base)]",
  md: {
    objectFit: "[var(--gbv-fit-md, var(--gbv-fit-base))]",
    objectPosition: "[var(--gbv-pos-md, var(--gbv-pos-base))]"
  },
  xl: {
    objectFit: "[var(--gbv-fit-xl, var(--gbv-fit-md, var(--gbv-fit-base)))]",
    objectPosition: "[var(--gbv-pos-xl, var(--gbv-pos-md, var(--gbv-pos-base)))]"
  }
});
function resolveFitPos(value, fallbackBase) {
  const resolved = value && typeof value === "object" ? v8ResponsiveToPanda(value) : {
    base: typeof value === "string" ? value : void 0
  };
  const clean = (v) => v && v !== "none" ? v : void 0;
  return {
    base: clean(resolved.base) ?? fallbackBase,
    md: clean(resolved.md),
    xl: clean(resolved.xl)
  };
}
const BackgroundVideo = forwardRef(({
  size,
  position,
  className,
  style,
  ...props
}, ref) => {
  const fit = resolveFitPos(size, "cover");
  const pos = resolveFitPos(position, "50% 50%");
  return /* @__PURE__ */ jsx(Video, { ref, className: `${baseBackgroundVideoClass} ${fitPositionClass}${className ? ` ${className}` : ""}`, style: {
    "--gbv-fit-base": fit.base,
    ...fit.md ? {
      "--gbv-fit-md": fit.md
    } : {},
    ...fit.xl ? {
      "--gbv-fit-xl": fit.xl
    } : {},
    "--gbv-pos-base": pos.base,
    ...pos.md ? {
      "--gbv-pos-md": pos.md
    } : {},
    ...pos.xl ? {
      "--gbv-pos-xl": pos.xl
    } : {},
    ...style
  }, ...props });
});
BackgroundVideo.displayName = "BackgroundVideo";
const MobileViewportVideo = forwardRef((props, ref) => {
  const {
    hideOnMobile,
    className,
    ...rest
  } = props;
  const displayStyles = {
    display: hideOnMobile ? "none" : "block",
    lg: {
      display: "none"
    }
  };
  return /* @__PURE__ */ jsx(BackgroundVideo, { ...rest, ref, className: `${css(displayStyles)} exclude-from-algorithmic-darkening${className ? ` ${className}` : ""}` });
});
MobileViewportVideo.displayName = "MobileViewportVideo";
const TabletViewportVideo = forwardRef((props, ref) => {
  const {
    hideOnMobile,
    hideOnTablet,
    className,
    ...rest
  } = props;
  const displayStyles = {
    display: hideOnMobile ? "none" : "block",
    lg: {
      display: hideOnTablet ? "none" : "block"
    },
    xl: {
      display: "none"
    }
  };
  return /* @__PURE__ */ jsx(BackgroundVideo, { ...rest, ref, className: `${css(displayStyles)} exclude-from-algorithmic-darkening${className ? ` ${className}` : ""}` });
});
TabletViewportVideo.displayName = "TabletViewportVideo";
const DesktopViewportVideo = forwardRef((props, ref) => {
  const {
    hideOnMobile,
    hideOnTablet,
    hideOnDesktop,
    className,
    ...rest
  } = props;
  const displayStyles = {
    display: hideOnMobile ? "none" : "block",
    lg: {
      display: hideOnTablet ? "none" : "block"
    },
    xl: {
      display: hideOnDesktop ? "none" : "block"
    }
  };
  return /* @__PURE__ */ jsx(BackgroundVideo, { ...rest, ref, className: `${css(displayStyles)} exclude-from-algorithmic-darkening${className ? ` ${className}` : ""}` });
});
DesktopViewportVideo.displayName = "DesktopViewportVideo";
const GroupBackgroundVideo = ({
  position = {
    default: "50% 50%"
  },
  size = {
    default: "cover"
  },
  source,
  visible = {
    default: true,
    lg_lte: true,
    sm_lte: true
  }
}) => {
  var _a, _b, _c;
  const muteVideoOnMountRef = useMuteVideoOnMountRef();
  const sourceMap = source;
  const desktopSource = ((_a = sourceMap == null ? void 0 : sourceMap.default) == null ? void 0 : _a.src) ? sourceMap.default : void 0;
  const tabletSource = ((_b = sourceMap == null ? void 0 : sourceMap.lg_lte) == null ? void 0 : _b.src) ? sourceMap.lg_lte : void 0;
  const mobileSource = ((_c = sourceMap == null ? void 0 : sourceMap.sm_lte) == null ? void 0 : _c.src) ? sourceMap.sm_lte : void 0;
  const hasBackgroundVideo = desktopSource || tabletSource || mobileSource;
  if (!hasBackgroundVideo) {
    return null;
  }
  const visibleMap = visible;
  const commonVideoProps = {
    autoPlay: true,
    muted: true,
    loop: true,
    playsInline: true,
    size,
    position
  };
  const hasMobileVideo = !!mobileSource;
  const hasTabletVideo = !!tabletSource;
  const isVisibleOnDesktop = !!(visibleMap == null ? void 0 : visibleMap.default);
  const isVisibleOnTablet = !!(visibleMap == null ? void 0 : visibleMap.lg_lte);
  const isVisibleOnMobile = !!(visibleMap == null ? void 0 : visibleMap.sm_lte);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    hasMobileVideo && /* @__PURE__ */ jsx(MobileViewportVideo, { ...commonVideoProps, hideOnMobile: !isVisibleOnMobile, className: "mobile-video", ref: muteVideoOnMountRef, children: /* @__PURE__ */ jsx(Source, { src: mobileSource == null ? void 0 : mobileSource.src }) }),
    hasTabletVideo && /* @__PURE__ */ jsx(TabletViewportVideo, { hideOnMobile: !isVisibleOnMobile || hasMobileVideo, hideOnTablet: !isVisibleOnTablet, ...commonVideoProps, className: "tablet-video", ref: muteVideoOnMountRef, children: /* @__PURE__ */ jsx(Source, { src: tabletSource == null ? void 0 : tabletSource.src }) }),
    desktopSource && /* @__PURE__ */ jsx(DesktopViewportVideo, { ...commonVideoProps, className: "desktop-video", hideOnMobile: !isVisibleOnMobile || hasMobileVideo || hasTabletVideo, hideOnTablet: !isVisibleOnTablet || hasTabletVideo, hideOnDesktop: !isVisibleOnDesktop, ref: muteVideoOnMountRef, children: /* @__PURE__ */ jsx(Source, { src: desktopSource.src }) })
  ] });
};
function useMuteVideoOnMountRef() {
  return useCallback(function muteVideoOnMount(ref) {
    if (!ref) return;
    ref.setAttribute("muted", "");
  }, []);
}
export {
  GroupBackgroundVideo as default
};
